import { Injectable, NgZone } from '@angular/core';
import { IVerificationContext } from '@core/models';
import { Observable, interval, map, takeWhile } from 'rxjs';
import { Logger } from './logger';

const log = new Logger('VerificationTimerService');

@Injectable({ providedIn: 'root' })
export class VerificationTimerService {
  constructor(private ngZone: NgZone) {}

  setCooldownEnd(ctx: IVerificationContext, durationSec: number) {
    const key = this.getCooldownKey(ctx);
    this.removeLegacyKey(ctx);
    const until = Date.now() + durationSec * 1000;
    sessionStorage.setItem(key, String(until));
    log.debug('[VerificationTimer] cooldown set', { key, durationSec, until });
  }

  // Returns remaining seconds; 0 if expired; defaultDuration only when no prior key (first visit)
  getCooldownRemaining(ctx: IVerificationContext, defaultDuration: number): number {
    const key = this.getCooldownKey(ctx);
    const raw = sessionStorage.getItem(key);
    if (!raw) return defaultDuration; // no key = first run
    const remaining = Math.ceil((Number(raw) - Date.now()) / 1000);
    return remaining > 0 ? remaining : 0; // expired => 0
  }

  clearCooldown(ctx: IVerificationContext): void {
    if (!ctx) return;
    const key = this.getCooldownKey(ctx);
    sessionStorage.removeItem(key);
    this.removeLegacyKey(ctx);
    log.debug('[VerificationTimer] cooldown cleared', { key });
  }

  countdown(seconds: number): Observable<number> {
    return new Observable<number>(observer => {
      this.ngZone.runOutsideAngular(() => {
        const sub = interval(1000)
          .pipe(
            map(i => seconds - i),
            takeWhile(s => s >= 0)
          )
          .subscribe({
            next: s => this.ngZone.run(() => observer.next(s)),
            complete: () => this.ngZone.run(() => observer.complete())
          });
        return () => sub.unsubscribe();
      });
    });
  }

  private getCooldownKey(ctx: IVerificationContext) {
    // optionally isolate concurrent flows with ctx.nonce/flowId if you have it
    const nonce = (ctx as any).nonce || (ctx as any).flowId || 'v1';
    return `resendCooldownUntil:${ctx.actionType}:${ctx.otpType}:${nonce}`;
  }

  private removeLegacyKey(ctx: IVerificationContext) {
    const old = `resendCooldownUntil:${ctx.actionType}:${ctx.otpType}:${
      (ctx as any).destination ?? ''
    }`;
    try {
      sessionStorage.removeItem(old);
    } catch {}
  }
}
